/**
 * Club Org Provisioning — Phase 2A
 *
 * Handles post-purchase org creation for club subscribers.
 * All helpers are idempotent and safe to call multiple times.
 *
 * Doctrine: DUEL_CLUB_MODE_ARCHITECTURE.md
 * - `organizations` is the canonical table for Club / School Mode.
 * - `gyms` is NOT touched by this module.
 *
 * Event flow:
 *   Purchase → RevenueCat → syncRevenueCatTierToProfile (sets org_pro)
 *   → reconcileClubOrg() (client-side, called on app launch / post-purchase)
 *   → provisionClubOrg() → organizations + organization_memberships rows
 *
 * Server-side: supabase/functions/revenuecat-webhook/index.ts (belt-and-suspenders)
 */

import { supabase } from '@/lib/supabase';
import { createLogger } from '@/lib/logger';
import type { OrgType } from '@/types/database';

// Coach org_type: 'coaching' is added by migration 20260405000600.
// types/database.ts OrgType union updated to include 'coaching'.
const COACH_ORG_TYPE: OrgType = 'coaching';

const log = createLogger('ORG_PROVISION');

/** Club entitlement IDs that trigger org provisioning */
const CLUB_ENTITLEMENTS = new Set([
  'club_starter',
  'club_growth',
  'club_performance',
  'club_elite',
  'club', // RevenueCat entitlement group key
  'org_pro', // normalized tier stored on profile
]);

export interface ProvisionResult {
  orgId: string;
  created: boolean; // true = newly created, false = already existed
}

// ─── Core provisioning helper ──────────────────────────────────────────────

/**
 * Provision an org for a club subscriber.
 *
 * Idempotent: if the user already owns an org (role=owner, status=active),
 * returns that org's id without creating a duplicate.
 *
 * Steps:
 *  1. Check for existing owned org via organization_memberships
 *  2. If found → return existing org_id (no writes)
 *  3. If not found → insert into organizations, then insert owner membership
 *
 * @param userId   Supabase auth user id
 * @param options  Optional overrides for name and org_type
 */
export async function provisionClubOrg(
  userId: string,
  options?: { name?: string; orgType?: OrgType },
): Promise<ProvisionResult> {
  if (!userId) throw new Error('provisionClubOrg: userId is required');

  // ── Step 1: idempotency check ────────────────────────────────────────────
  const existing = await resolveClubOwnerOrg(userId);
  if (existing) {
    log.info('[ORG_PROVISION] org already exists, skipping creation', {
      userId: userId.slice(0, 8),
      orgId: existing,
    });
    return { orgId: existing, created: false };
  }

  // ── Step 2: create org row ───────────────────────────────────────────────
  const orgName = options?.name ?? 'Your Club';
  const orgType: OrgType = (options?.orgType as OrgType) ?? 'gym';

  // Phase 2E: school orgs auto-require minor consent (also enforced by Postgres trigger)
  const requiresMinorConsent = orgType === 'school';

  const { data: org, error: orgError } = await supabase
    .from('organizations')
    .insert({
      name: orgName,
      org_type: orgType,
      created_by: userId,
      parent_organization_id: null,
      setup_complete: false,
      requires_minor_consent: requiresMinorConsent,
    })
    .select('id')
    .single();

  if (orgError || !org) {
    log.error('[ORG_PROVISION] failed to create organization', orgError?.message);
    throw new Error(`Failed to create organization: ${orgError?.message ?? 'unknown error'}`);
  }

  const orgId = org.id as string;
  log.info('[ORG_PROVISION] organization created', { orgId, orgName, orgType });

  // ── Step 3: create owner membership row ─────────────────────────────────
  // Safe even if the org row was just created — insert with conflict guard.
  const { error: membershipError } = await supabase
    .from('organization_memberships')
    .upsert(
      {
        org_id: orgId,
        user_id: userId,
        role: 'owner',
        status: 'active',
        is_primary_for_type: true,
      },
      { onConflict: 'org_id,user_id' },
    );

  if (membershipError) {
    log.error('[ORG_PROVISION] failed to create owner membership', membershipError.message);
    // Org was created but membership failed — log and surface error.
    // Caller can retry; resolveClubOwnerOrg will find org via created_by on next attempt.
    throw new Error(`Failed to create owner membership: ${membershipError.message}`);
  }

  log.info('[ORG_PROVISION] owner membership created', {
    userId: userId.slice(0, 8),
    orgId,
  });

  return { orgId, created: true };
}

// ─── Owner context resolution ──────────────────────────────────────────────

/**
 * Returns the org_id of the user's primary owned org, or null.
 *
 * Queries organization_memberships for:
 *   role = 'owner', status = 'active'
 *   ordered by created_at ASC (oldest = primary)
 *
 * Returns the first result (primary org).
 * Returns null if no owned org found.
 *
 * Used by: app startup, post-purchase redirect, club setup gate.
 */
export async function resolveClubOwnerOrg(userId: string): Promise<string | null> {
  if (!userId) return null;

  const { data, error } = await supabase
    .from('organization_memberships')
    .select('org_id')
    .eq('user_id', userId)
    .eq('role', 'owner')
    .eq('status', 'active')
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) {
    log.warn('[ORG_PROVISION] resolveClubOwnerOrg query error', error.message);
    return null;
  }

  return (data?.org_id as string) ?? null;
}

// ─── Reconciliation / backfill safety ─────────────────────────────────────

/**
 * Reconcile: provision an org if the user has a club entitlement but no org.
 *
 * Safe to call on every app launch. No-ops if:
 * - entitlement is not a club entitlement
 * - user already has an owned org
 *
 * @param userId      Supabase auth user id
 * @param entitlement The entitlement key from the subscription system
 *                    (e.g. 'org_pro', 'club_starter', 'club_growth', etc.)
 */
// ─── Coach Org Provisioning ────────────────────────────────────────────────

/**
 * Returns the org_id of the user's owned 'coaching' org, or null.
 *
 * Queries organization_memberships for role='owner', status='active',
 * joins organizations WHERE org_type='coaching'.
 *
 * Used by: coach startup routing, settings screen, link creation.
 */
export async function resolveCoachOwnerOrg(userId: string): Promise<string | null> {
  if (!userId) return null;

  const { data, error } = await supabase
    .from('organization_memberships')
    .select('org_id, organizations!inner(id, org_type)')
    .eq('user_id', userId)
    .eq('role', 'owner')
    .eq('status', 'active')
    .eq('organizations.org_type', 'coaching')
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) {
    log.warn('[ORG_PROVISION] resolveCoachOwnerOrg query error', error.message);
    return null;
  }

  return (data?.org_id as string) ?? null;
}

/**
 * Provision a personal coaching org for a coach_pro subscriber.
 *
 * Idempotent: if the user already owns a 'coaching' org, returns its id.
 *
 * org_type='coaching' requires migration 20260405000600 to be applied.
 *
 * Steps:
 *  1. Check for existing owned coaching org
 *  2. If found → return existing org_id (no writes)
 *  3. If not found → insert organizations row, then insert owner membership
 *
 * @param userId  Supabase auth user id
 * @param name    Optional org name override (defaults to "Personal Coaching")
 */
export async function provisionCoachOrg(
  userId: string,
  name?: string,
): Promise<string | null> {
  if (!userId) return null;

  // Step 1: idempotency check
  const existing = await resolveCoachOwnerOrg(userId);
  if (existing) {
    log.info('[ORG_PROVISION] coach org already exists', {
      userId: userId.slice(0, 8),
      orgId: existing,
    });
    return existing;
  }

  // Step 2: create org row
  const orgName = name ?? 'Personal Coaching';

  const { data: org, error: orgError } = await supabase
    .from('organizations')
    .insert({
      name: orgName,
      org_type: COACH_ORG_TYPE, // 'coaching' — added by migration 20260405000600
      created_by: userId,
      parent_organization_id: null,
      setup_complete: true,         // Personal coach doesn't need the setup wizard
      requires_minor_consent: false,
    })
    .select('id')
    .single();

  if (orgError || !org) {
    log.error('[ORG_PROVISION] provisionCoachOrg: failed to create org', orgError?.message);
    return null;
  }

  const orgId = org.id as string;
  log.info('[ORG_PROVISION] coach org created', { orgId, orgName });

  // Step 3: create owner membership row
  const { error: membershipError } = await supabase
    .from('organization_memberships')
    .upsert(
      {
        org_id: orgId,
        user_id: userId,
        role: 'owner',
        status: 'active',
        is_primary_for_type: true,
      },
      { onConflict: 'org_id,user_id' },
    );

  if (membershipError) {
    log.error('[ORG_PROVISION] provisionCoachOrg: failed to create membership', membershipError.message);
    // Org was created — return orgId so caller can retry membership later
    return orgId;
  }

  log.info('[ORG_PROVISION] coach owner membership created', {
    userId: userId.slice(0, 8),
    orgId,
  });

  return orgId;
}

/**
 * Reconcile: provision a coaching org if the user has coach_pro but no org.
 *
 * Safe to call on every app launch — no-ops if org already exists.
 * Wraps provisionCoachOrg in try/catch; never throws.
 */
export async function reconcileCoachOrg(userId: string): Promise<void> {
  if (!userId) return;
  try {
    await provisionCoachOrg(userId);
  } catch (err: any) {
    log.warn('[ORG_PROVISION] reconcileCoachOrg: suppressed error', err?.message ?? err);
  }
}

// ─── Club Org Reconciliation ───────────────────────────────────────────────

export async function reconcileClubOrg(
  userId: string,
  entitlement: string,
): Promise<ProvisionResult | null> {
  if (!userId) return null;
  if (!CLUB_ENTITLEMENTS.has(entitlement)) return null;

  // Check if already provisioned
  const existingOrgId = await resolveClubOwnerOrg(userId);
  if (existingOrgId) {
    // Already provisioned — no-op
    return { orgId: existingOrgId, created: false };
  }

  log.info('[ORG_PROVISION] reconcileClubOrg: provisioning missing org', {
    userId: userId.slice(0, 8),
    entitlement,
  });

  try {
    return await provisionClubOrg(userId);
  } catch (err: any) {
    log.error('[ORG_PROVISION] reconcileClubOrg failed', err?.message ?? err);
    return null;
  }
}
